import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getTimezone } from './datetime'
import { getSchedulingIntervalEnd, getSchedulingIntervalStart } from './schedulingBounds'

dayjs.extend(utc)
dayjs.extend(timezone)

export const DAY_NIGHT_SPLIT_HOUR = 20

export type IntervalPresetId = 'day' | 'night' | 'full' | 'custom'

export type TimeInterval = {
  start: string
  end: string
}

export type IntervalPresets = {
  day: TimeInterval | null
  night: TimeInterval | null
  full: TimeInterval
}

const toIso = (value: dayjs.Dayjs): string => value.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

const clampToWindow = (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  windowStart: dayjs.Dayjs,
  windowEnd: dayjs.Dayjs
): TimeInterval | null => {
  const clampedStart = start.isAfter(windowStart) ? start : windowStart
  const clampedEnd = end.isBefore(windowEnd) ? end : windowEnd
  if (!clampedStart.isBefore(clampedEnd)) {
    return null
  }
  return { start: toIso(clampedStart), end: toIso(clampedEnd) }
}

export const intervalsMatch = (a: TimeInterval, b: TimeInterval): boolean =>
  dayjs.utc(a.start).isSame(dayjs.utc(b.start)) && dayjs.utc(a.end).isSame(dayjs.utc(b.end))

export const buildIntervalPresets = (windowStartIso: string, windowEndIso: string): IntervalPresets => {
  const tz = getTimezone()
  const windowStart = dayjs.utc(windowStartIso).tz(tz)
  const windowEnd = dayjs.utc(windowEndIso).tz(tz)
  const startTime = getSchedulingIntervalStart()
  const endTime = getSchedulingIntervalEnd()
  const dayAnchor = windowStart.startOf('day')
  const dayStart = dayAnchor.hour(startTime.hours).minute(startTime.minutes).second(0).millisecond(0)
  const split = dayAnchor.hour(DAY_NIGHT_SPLIT_HOUR).minute(0).second(0).millisecond(0)
  const nightEnd = dayAnchor.add(1, 'day').hour(endTime.hours).minute(endTime.minutes).second(0).millisecond(0)

  return {
    day: clampToWindow(dayStart, split, windowStart, windowEnd),
    night: clampToWindow(split, nightEnd, windowStart, windowEnd),
    full: { start: toIso(windowStart), end: toIso(windowEnd) },
  }
}

export const matchIntervalPreset = (interval: TimeInterval, presets: IntervalPresets): IntervalPresetId => {
  if (presets.day && intervalsMatch(interval, presets.day)) {
    return 'day'
  }
  if (presets.night && intervalsMatch(interval, presets.night)) {
    return 'night'
  }
  if (intervalsMatch(interval, presets.full)) {
    return 'full'
  }
  return 'custom'
}

export const intervalFromPreset = (
  presetId: IntervalPresetId,
  presets: IntervalPresets,
  fallback: TimeInterval
): TimeInterval => {
  if (presetId === 'day' && presets.day) {
    return presets.day
  }
  if (presetId === 'night' && presets.night) {
    return presets.night
  }
  if (presetId === 'full') {
    return presets.full
  }
  return fallback
}

const padClock = (value: number): string => String(value).padStart(2, '0')

export const zonedInstantToPickerDate = (iso: string, timeZone: string = getTimezone()): Date => {
  const zoned = dayjs.utc(iso).tz(timeZone)
  return new Date(zoned.year(), zoned.month(), zoned.date(), zoned.hour(), zoned.minute(), zoned.second())
}

export const pickerDateToZonedIso = (value: Date | string, timeZone: string = getTimezone()): string => {
  if (typeof value === 'string') {
    return dayjs.tz(value, timeZone).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  }
  const stamp = `${value.getFullYear()}-${padClock(value.getMonth() + 1)}-${padClock(value.getDate())}T${padClock(value.getHours())}:${padClock(value.getMinutes())}:${padClock(value.getSeconds())}`
  return dayjs.tz(stamp, timeZone).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

export const formatIntervalClock = (interval: TimeInterval, timeZone: string = getTimezone()): string => {
  const start = dayjs.utc(interval.start).tz(timeZone).format('HH:mm')
  const end = dayjs.utc(interval.end).tz(timeZone).format('HH:mm')
  return `${start}–${end}`
}

export const defaultPopulateInterval = (windowStartIso: string, windowEndIso: string): TimeInterval => {
  const presets = buildIntervalPresets(windowStartIso, windowEndIso)
  return presets.day ?? presets.full
}
